// Copyright (c) Microsoft. All rights reserved.

import { JwtPayload, jwtDecode } from 'jwt-decode';
import { ActiveUserInfo } from '../../redux/features/app/AppState';
import { IChatUser } from '../models/ChatUser';

export const enum AuthType {
    None = 'None',
    OIDC = 'OpenIdConnect',
}

// This is the default user information when authentication is set to 'None'.
// It must match what is defined in PassthroughAuthenticationHandler.cs on the backend.
export const DefaultChatUser: IChatUser = {
    id: 'c05c61eb-65e4-4223-915a-fe72b0c9ece1',
    emailAddress: 'user@contoso.com',
    fullName: 'Default User',
    online: true,
};

export const DefaultActiveUserInfo: ActiveUserInfo = {
    id: DefaultChatUser.id,
    email: DefaultChatUser.emailAddress,
    username: DefaultChatUser.fullName,
    sessionId: null,
};

function getAuthType(): AuthType {
    switch (process.env.REACT_APP_AUTH_TYPE) {
        case AuthType.OIDC:
            return AuthType.OIDC;
    }
    return AuthType.None;
}

function getSessionIdFromToken(token: string): string | null {
    const decoded = jwtDecode<JwtPayload & { sid: string }>(token);
    return decoded.sid;
}

type LogoutFunction = () => Promise<void>;

// This global logout function is used to provide logout functionality to the app to
// the places where we don't have access to the Auth object. Specifically, this is
// used in the signalr connection when receiving a token denied event, and in the BaseService
// when reciving a message that the token has been revoked.
let globalLogout: LogoutFunction | null = null;

function setLogoutFunction(logoutFn: LogoutFunction) {
    globalLogout = logoutFn;
}

// Resolves true only when a logout actually ran. Callers use the answer to decide whether the user
// has been told anything: useAuthSetup registers the function from an effect gated on
// Auth.isAuthenticated, so a caller can reach this before (or after) there is anything to call, and
// reporting success then would leave a revoked session with no logout and no message.
async function logoutForBlockedToken(): Promise<boolean> {
    if (!globalLogout) {
        console.warn('No logout function has been set');
        return false;
    }

    await globalLogout();
    return true;
}

export const AuthHelper = {
    getAuthType,
    getSessionIdFromToken,
    setLogoutFunction,
    logoutForBlockedToken,
} as const;
